import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { getAuth, confirmPasswordReset } from "firebase/auth";
import { app } from "../..";
import { useToasts } from "../../contexts/toasts";
import { Form, Input } from "antd";
import { Col, Container, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faSignIn } from "@fortawesome/free-solid-svg-icons";
import Logo from "../../common/Logo";
import logo from "../../assets/logo/logo.png";
import classNames from "classnames";
import { User } from "../../api/User";

export default function ResetPassword() {
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const [verifyForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [oobCode, setOobCode] = useState<string>();
  const [email, setEmail] = useState<string>();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      Cookies.remove(window.__env.AUTH_TOKEN_NAME || "get-move-token");
      Cookies.remove(window.__env.AUTH_USER_NAME || "get-move-user");
      navigate("/");
    } else {
      User.validateUserToken({ token: token! }).then(
        ({ data }) => {
          setOobCode(data.oobCode);
          setEmail(data.email);
        },
        () => {
          showToast(
            "verify-user-error",
            "Errore nella validazione dell'utente.",
            "",
            "danger"
          );
          navigate("/");
        }
      );
    }
  }, [navigate, showToast]);

  const handleFormFinish = useCallback(
    async (values: any) => {
      const { password } = values;
      const auth = getAuth(app);
      setLoading(true);
      confirmPasswordReset(auth, oobCode!, password)
        .then(() => {
          User.resetPassword({ email: email! }).then(
            () => {
              setLoading(false);
              showToast(
                "confirm-user-success",
                "Hai reimpostato correttamente la password! Torna in app e scopri un mondo di sfide!",
                "",
                "success"
              );
              navigate("/");
            },
            () => {
              setLoading(false);
              showToast(
                "confirm-user-error",
                "Errore nel reset della password. Riprova più tardi",
                "",
                "danger"
              );
              navigate("/");
            }
          );
        })
        .catch(() => {
          showToast(
            "confirm-user-error",
            "Errore nel reset della password. Riprova più tardi",
            "",
            "danger"
          );
          navigate("/");
        });
    },
    [oobCode, email, showToast, navigate]
  );

  return (
    <Container className="mt-5 justify-content-center text-center">
      <Logo logo={logo} width={200} className="my-4" />
      <h3 className="mb-5">Scegli la nuova password:</h3>
      <Form
        className="align-items-center"
        form={verifyForm}
        onFinish={handleFormFinish}
      >
        <Form.Item
          labelAlign="left"
          labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
          wrapperCol={{
            xs: { span: 24 },
            md: { span: 8, offset: 8 },
          }}
          name="password"
          label="Password"
          rules={[
            {
              required: true,
              message: "La password è obbligatoria",
            },
          ]}
        >
          <Input.Password
            size="large"
            placeholder="Password..."
            type="password"
            iconRender={(visible) => (
              <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
            )}
          />
        </Form.Item>
        <Form.Item
          labelAlign="left"
          labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
          wrapperCol={{
            xs: { span: 24 },
            md: { span: 8, offset: 8 },
          }}
          dependencies={["password"]}
          name="passwordConfirmation"
          label="Conferma Password"
          rules={[
            {
              required: true,
              message: "La Conferma Password è obbligatoria",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Le password non coincidono"));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Conferma Password..."
            size="large"
            type="password"
            iconRender={(visible) => (
              <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
            )}
          />
        </Form.Item>
        <Col xs={{ span: 24 }} md={{ span: 4, offset: 4 }}>
          <button
            type="submit"
            disabled={loading}
            className={classNames("w-100 mt-5 button-primary", {
              "button-disabled": loading,
            })}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FontAwesomeIcon icon={faSignIn} />
                <span className="ms-2">Conferma</span>
              </>
            )}
          </button>
        </Col>
      </Form>
    </Container>
  );
}
